import { computed, ref } from 'vue'
import { defineStore } from 'pinia'
import { APIClient } from '../utils/api-client/APIClient'
import { CurrentUserSchema, type User } from '@/models/user.ts'
import type { Config } from '@/models/user.ts'

/**
 * Provides the user currently logged into the practice portal.
 * Contains all interaction with login, logout, profile changes, etc.
 */
export const useUserStore = defineStore('user', () => {
  const user = ref<User | null>(null)
  const config = ref<Config | null>(null)
  const isLoggedIn = computed(() => user.value !== null)

  /**
   * Attempts a login into the therapist's portal.
   * @param username
   * @param password
   * @throws Throws when a network issue occurs or the API is down
   */
  const logIn = async function (username: string, password: string): Promise<boolean> {
    const result = await APIClient.login(username, password)
    if (result !== false) {
      user.value = result
    }
    return result !== false
  }

  /**
   * Initializes an empty user store by fetching /user from the API
   */
  const initialize = async function (): Promise<void> {
    try {
      const response = await APIClient.requestWithModel(CurrentUserSchema, 'user', 'GET')
      user.value = response.user
      config.value = response.config
    } catch {
      // Intentionally left blank
      // An error here can be caught silently as the user will get redirected to the login
      // route if this call fails
    }
  }

  /**
   * Invalidates the currently logged in user. Should be called whenever we get an unexpected 401 from the API
   */
  const invalidate = function (): void {
    user.value = null
  }

  return { user, config, isLoggedIn, logIn, initialize, invalidate }
})
